import { useEffect, useRef, type FC } from "react";
import { ErrorBoundary } from "@components/ErrorBoundary";
import { Header } from "@components/Header";
import { Footer } from "@components/Footer";
import { PrescriptionStep } from "@components/steps/PrescriptionStep";
import { AddProductStep } from "@components/steps/AddProductStep";
import { ManualInputStep } from "@components/steps/ManualInputStep";
import { Layout } from "@components/Layout";
import { StepContainer } from "./components/StepContainer";
import { ProductPreview } from "./components/ProductPreview";
import { ModalManager } from "./components/UI/Modal";
import { MyAccountPrescriptionStep } from "@components/steps/MyAccountPrescriptionStep";
import { StepSkeleton } from "./components/StepSkeleton";
import { useWorkflow } from "@hooks/useWorkflow";
import { useConfigStore, useStepStore } from "@store/index";
import { STEP_ID } from "./types/steps.types";
import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "@/i18n";
import InsuranceCard from "./components/InsuranceCard";

// Import global styles
import "@styles/reset.css";
import "@styles/global.css";

// Import i18n configuration
import "@/i18n/config";
import { useWidgetBlock } from "./hooks/useWidgetBlock";

declare global {
  interface Window {
    CLConfiguratorWidget: unknown;
    __clWidgetInstance?: { close: () => void };
    freshRelevancePageChange?: (source: string) => boolean;
  }
}

export const Widget: FC = () => {
  const { config, isLoading, error } = useConfigStore();
  const { isManualInput } = useStepStore();
  const { currentStep, handleBack, canGoBack, canGoNext } = useWorkflow();
  const { t } = useTranslation();

  const widgetRef = useRef<HTMLDivElement>(null);
  const isFirstFreshRelevanceRender = useRef(true);
  useWidgetBlock(widgetRef);

  // Load theme based on brand configuration (non-blocking, cached)
  const { error: themeError } = useTheme(config?.brand);

  // Log theme errors but don't block rendering
  if (themeError) {
    console.error("Theme loading error:", themeError);
  }

  useEffect(() => {
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const currentStepId = currentStep?.id;

    if (!currentStepId) {
      return;
    }

    if (isFirstFreshRelevanceRender.current) {
      isFirstFreshRelevanceRender.current = false;
      return;
    }

    window.freshRelevancePageChange?.(
      `cl-configurator:${currentStepId}`,
    );
  }, [currentStep?.id]);

  const renderStep = () => {
    switch (currentStep?.id) {
      case STEP_ID.PRESCRIPTION_TYPE:
        return (
          <PrescriptionStep
            title={currentStep?.title || "title missing"}
            description={currentStep?.description}
          />
        );
      case STEP_ID.ADVANCED_PRESCRIPTION:
        if (!isManualInput) {
          return (
            <MyAccountPrescriptionStep
              title={currentStep?.accountTitle || "title missing"}
              description={currentStep?.accountDescription}
            />
          );
        } else {
          return (
            <ManualInputStep
              title={currentStep?.manualTitle || "title missing"}
              description={currentStep?.manualDescription}
            />
          );
        }
      case STEP_ID.ADD_PRODUCT:
        return (
          <AddProductStep
            title={currentStep?.title || "title missing"}
            description={currentStep?.description}
          />
        );
      default:
        return <StepSkeleton />;
    }
  };

  const handleExit = () => {
    window.__clWidgetInstance?.close();
  };

  return (
    <ErrorBoundary
      onError={console.error}
      errorTitle={t("errors.genericTitle")}
      errorDescription={t("errors.genericDescription")}
      tryAgainText={t("errors.tryAgainButton")}
    >
      <div
        className="cl-configurator clcContainer"
        data-loading={isLoading}
        data-error={error !== null}
        ref={widgetRef}
      >
        <ModalManager />
        <Layout>
          <ProductPreview />
          <StepContainer>
            <Header
              backAvailable={canGoBack}
              onBack={handleBack}
              onClose={handleExit}
              showProgress={canGoNext || canGoBack}
            />
            <div className="cl-mobile-only">
              <InsuranceCard />
            </div>

            {renderStep()}

            <Footer />
          </StepContainer>
        </Layout>
      </div>
    </ErrorBoundary>
  );
};
